import angular from "angular";
import moment from "moment";

angular
    .module('ui-sentinel.devices.latestDeviceTracking')
    .factory('LatestDeviceTrackingReportsService', LatestDeviceTrackingReportsService);

LatestDeviceTrackingReportsService.$inject = ['$rootScope', '$q', 'SentinelUiSession', 'localStorageService', 'TrackingFilterService', 'ClientReportsService', 'ShipmentsReportService', 'SentryAdminApiService', 'SentryAccountApiService', 'UtilityService', 'BatteryPercentageService'];

function LatestDeviceTrackingReportsService($rootScope, $q, SentinelUiSession, localStorageService, TrackingFilterService, ClientReportsService, ShipmentsReportService, SentryAdminApiService, SentryAccountApiService, UtilityService, BatteryPercentageService) {
    const maxPageSize = 5000;
    const maxReportCapacity = 5000;
    const sentryDeviceType = 'Sentry';
    const sentinelDeviceType = 'Sentinel';
    // SidewalkWebhook origin bit of the backend's report trace bitfield
    const sidewalkTraceBit = 1 << 10;
    let totalCount = 0;

    const defaultDays = $rootScope.isOrs ? 365 : 7;
    const maxDays = $rootScope.isOrs ? 368 : 45;

    const service = {
        filter: TrackingFilterService,
        dateKey: 'deviceTrackingFilter',
        lastClientGuid: null,
        reports: [],
        sentinelReports: [],
        sentryReports: [],
        lastReport: null,
        selected: null,
        isOrs: $rootScope.isOrs,
        load: load,
        init: init,
        get: getReport,
        defaultDays,
        maxDays,
        fromDate: moment().subtract(defaultDays, 'day').startOf('day'),
        toDate: moment().add(1, 'day').endOf('day'),
        fromDateCache: moment().subtract(defaultDays, 'day').startOf('day'),
        toDateCache: moment().add(1, 'day').endOf('day'),
        reportsCache: [],
        dataWarning: '',
        dataError: '',
        isFull: false,
        clear: clear,
        restartCache: restartCache,
        searchPattern: searchPattern,
        filterData: filterData,
    };
    activate();
    return service;

    ///////////////////////////////////////////////////

    function activate() {
        // in case this is a page reload event, check for 'cached' dates and use those
        const savedFromDate = UtilityService.getSetting(`${service.dateKey}.fromDate`);
        const savedToDate = UtilityService.getSetting(`${service.dateKey}.toDate`);
        if (savedFromDate) {
            service.fromDate = moment(savedFromDate);
        }
        if (savedToDate) {
            service.toDate = moment(savedToDate);
        }

        $rootScope.$on('CLIENT_FOCUS_CHANGED', function (event, args) {
            clear();
        });
    }

    function restartCache(forceUseLastDateRange) {
        clear(forceUseLastDateRange);
    }

    function clear(forceUseLastDateRange) {
        service.filter.searchText = '';
        service.filter.filterByDeviceType = '';
        if (!forceUseLastDateRange || forceUseLastDateRange !== true) {
            service.lastClientGuid = null;
            service.selected = null;
            service.reports = [];
            service.isFull = false;
            service.dataWarning = '';
            service.dataError = '';
            service.lastReport = null;

            const savedFromDate = UtilityService.getSetting(`${service.dateKey}.fromDate`);
            const savedToDate = UtilityService.getSetting(`${service.dateKey}.toDate`);

            service.fromDate = savedFromDate ? moment(savedFromDate) :  defaultFromDate();
            service.toDate = savedToDate ? moment(savedToDate) : defaultToDate();
        }
    }

    function loadingBegin() {
        service.dataWarning = '';
        service.dataError = '';
        $rootScope.loading = true;
    }

    function loadingComplete() {
        if (service.reports.length === 0 && !service.dataWarning && !service.dataError) {
            service.dataWarning = 'There are no device reports for this date range';
        } else if (service.reports.length > 0) {
            service.dataWarning = '';
            service.dataError = '';
        }
        $rootScope.loading = false;
    }

    function floorFigure(figure, decimals) {
        if (!decimals) decimals = 2;
        const d = Math.pow(10, decimals);
        return (parseInt(figure * d) / d).toFixed(decimals);
    }

    async function promiseWithRetry(promiseGenerator, limit, accept404) {
        let attempt = 0;
        let exception = undefined;
        const wait = interval => new Promise(resolve => setTimeout(resolve, interval));
        while (attempt < limit) {
            if (attempt > 0) {
                await wait(500);
            }
            try {
                return await promiseGenerator();
            } catch (e) {
                if (e.status === 404) {
                    if (accept404) {
                        return undefined;
                    } else {
                        throw e;
                    }
                }
                exception = e;
                attempt++;
            }
        }

        throw exception;
    }

    function addSentinelProperties(sentry, sentinel) {
        sentry.type = sentinelDeviceType;
        sentry.deviceName = sentinel.sentinelFriendlyName ? sentinel.sentinelFriendlyName : sentinel.sightingId;
        sentry.deviceTagId = sentinel.sightingId;
        sentry.humidity = sentinel.humidity;
        sentry.beaconType = sentinel.beaconType;
        sentry.pressure = sentinel.pressure;
        sentry.shockMagnitude = sentinel.shockMagnitude;
        sentry.shockElapsedTime = sentinel.shockElapsedTime;
        sentry.shockX = sentinel.shockX;
        sentry.shockY = sentinel.shockY;
        sentry.shockZ = sentinel.shockZ;
        sentry.tiltX = sentinel.tiltX;
        sentry.tiltY = sentinel.tiltY;
        sentry.tiltZ = sentinel.tiltZ;
        sentry.bleLocation = sentinel.bleLocation;
        sentry.timeOfReceipt = sentinel.timeOfReceipt;
        sentry.timeOfReport = sentinel.timeOfReport;
        sentry.messageTimeStamp = sentinel.timeOfReport;
        sentry.serverTimeStamp = sentinel.timeOfReceipt;
        sentry.batteryPercent = BatteryPercentageService.calc(sentinel.batteryVoltage, sentinel.beaconType);
        sentry.light = sentinel.light;
        sentry.temperatureC = sentinel.temperature;
        sentry.temperatureF = sentinel.temperature ? floorFigure(((sentinel.temperature * (9 / 5)) + 32)) : null;
        sentry.dewPointC = sentinel.dewPointC;
        sentry.dewPointF = sentinel.dewPointF;
        sentry.extSwitch = sentinel.extSwitch;
        sentry.temperatureProbe1C = sentinel.externalTemperature;
        sentry.temperatureProbe1F = sentinel.externalTemperature ? floorFigure(((sentinel.externalTemperature * (9 / 5)) + 32)) : null;
        sentry.temperatureProbe2C = sentinel.externalSensor1;
        sentry.temperatureProbe2F = sentinel.externalSensor1 ? floorFigure(((sentinel.externalSensor1 * (9 / 5)) + 32)) : null;
        sentry.totalSightingsCount = "";
        sentry.reportGuid = sentinel.id;
        sentry.sentryStatusId = sentinel.sentryStatusId || sentinel.sentryStatusID;
        if (sentry.sentryFriendlyName) {
            sentry.deviceName = sentinel.sentryFriendlyName;
        }
        sentry.isShockExceeded = !!sentry.shockMagnitude;
    }

    function copySentry(guid, sentries, sentinel) {
        const sentry = _.find(sentries, function (sentry) {
            return sentry.deviceTagId === sentinel.imei;
        });
        if (!sentry) {
            console.log("reportGuid doesn't exist", sentinel.sentryStatusID);
            return undefined;
        }
        const s = angular.copy(sentry);
        s.guid = guid;
        addSentinelProperties(s, sentinel);
        return s;
    }

    function makeSentry(guid, sentinel) {
        const sentry = {};
        sentry.guid = guid;
        addSentinelProperties(sentry, sentinel);
        sentry.severity = 'ok';
        sentry.latitude = sentinel.latitude;
        sentry.longitude = sentinel.longitude;
        sentry.locationMethod = sentinel.locationMethod;
        return sentry;
    }

    // Sidewalk tags report as their own gateway: the backend auto-creates a Sentry keyed on the
    // Sentinel's id and keeps all sensor data on the Sentinel, so that Sentry is an empty shell —
    // hide it and show only the Sentinel. Sentries from other sources that share a tagId (LoRaWAN,
    // FlexTag, SuperSentinel, sentinel-as-sentry) are real devices carrying the same readings, so
    // they are kept. Sentry reports carry no trace, so the Sentinel's trace identifies the source.
    function isSyntheticSentry(sentry, sidewalkTagIds) {
        return sidewalkTagIds.has(sentry.deviceTagId);
    }

    function buildItems(sentries, sentinelStatus) {
        let guid = new Date().getTime();
        const items = [];
        const sidewalkTagIds = new Set(sentinelStatus
            .filter(sentinel => (sentinel.trace & sidewalkTraceBit) !== 0)
            .map(sentinel => sentinel.sightingId));

        for (const item of sentries) {
            if (isSyntheticSentry(item, sidewalkTagIds)) {
                continue;
            }
            item.guid = guid++;
            item.type = "Sentry";
            item.timeOfReceipt = item.serverTimeStamp;
            item.timeOfReport = item.messageTimeStamp;
            item.isShockExceeded = item.shockMagnitude != null || (item.shockX != null && item.shockY != null && item.shockZ != null);

            if (item.isShockExceeded && item.shockMagnitude == null) {
                item.shockMagnitude = Math.sqrt(Math.pow(item.shockX, 2) + Math.pow(item.shockY, 2) + Math.pow(item.shockZ, 2)).toFixed(1);
            }

            item.isTiltExceeded = (item.tiltX != null && item.tiltY != null && item.tiltZ != null);

            if (item.isTiltExceeded) {
                item.tiltMagnitude = Math.sqrt(Math.pow(item.tiltX, 2) + Math.pow(item.tiltY, 2) + Math.pow(item.tiltZ, 2)).toFixed(1);
            }

            items.push(item);
        }

        for (const sentinel of sentinelStatus) {
            guid++;
            const hasLocation = sentinel.latitude !== null && sentinel.latitude !== undefined && sentinel.longitude !== null && sentinel.longitude !== undefined;
            const sentry = hasLocation ? makeSentry(guid, sentinel) : copySentry(guid, sentries, sentinel);
            if (sentry) {
                items.push(sentry);
            } else {
                console.log("reportGuid doesn't exist and sentinel has no location", sentinel.sentryStatusID);
            }
        }

        return items;
    }

    // Sentry is the default view; if there are no Sentries but there are Sentinels, show Sentinels instead
    function defaultDeviceTypeFilter(items) {
        const hasSentries = items.some(item => item.type === sentryDeviceType);
        const hasSentinels = items.some(item => item.type === sentinelDeviceType);
        return !hasSentries && hasSentinels ? sentinelDeviceType : sentryDeviceType;
    }

    function defaultToDate() {
        let toDate = service.lastReport && service.lastReport.timeOfReport
            ? moment(service.lastReport.timeOfReport).add(1, 'day')
            : moment().add(1, 'day');
        if (service.isOrs) {
            toDate = toDate.utc();
        }
        return toDate.endOf('day');
    }

    function defaultFromDate() {
        let fromDate = service.lastReport && service.lastReport.timeOfReport
            ? moment(service.lastReport.timeOfReport).subtract(7, 'day')
            : moment().subtract(service.defaultDays, 'day');
        if (service.isOrs) {
            fromDate = fromDate.utc();
        }
        return fromDate.startOf('day');
    }

    async function init() {
        clear();

        const sentinelReportPromise = () => {
            if (SentinelUiSession.user.isSystemAdmin && SentinelUiSession.user.accountId === SentinelUiSession.focus.id) {
                return SentryAdminApiService.lastSentry500SentinelReport(SentinelUiSession.focus).$promise;
            } else if (SentinelUiSession.user.isSystemAdmin) {
                return SentryAdminApiService.lastSentry500SentinelReportByAccount(SentinelUiSession.focus).$promise;
            } else {
                return SentryAccountApiService.latestSentry500SentinelReportByAccount(SentinelUiSession.focus).$promise;
            }
        }

        loadingBegin();

        try {
            const lastReport = await promiseWithRetry(sentinelReportPromise, 3, true);

            console.log('lastReport', lastReport);
            service.lastReport = lastReport && !lastReport.status === 404 ? lastReport : undefined;
            service.lastid = SentinelUiSession.focus.id;
            service.selected = null;

            // set the default dates based on the latest report
            if (service.lastReport) {
                console.log("get from latest");
                const savedFromDate = UtilityService.getSetting(`${service.dateKey}.fromDate`);
                const savedToDate = UtilityService.getSetting(`${service.dateKey}.toDate`);
                service.toDate = savedToDate ? moment(savedToDate) : defaultToDate();
                service.fromDate = savedFromDate ? moment(savedFromDate) : defaultFromDate();
            }

            totalCount = 0;

            service.fromDateCache = service.fromDate.clone();
            service.toDateCache = service.toDate.clone();

            const sentry500ReportPromise = () => {
                return SentinelUiSession.user.isAnAdmin ?
                    ClientReportsService.getLatestSentry500ReportsByClient(SentinelUiSession.focus, service.fromDate, service.toDate, 1, maxPageSize).$promise :
                    ClientReportsService.getLatestSentry500Reports(SentinelUiSession.focus, service.fromDate, service.toDate, 1, maxPageSize).$promise;
            }

            const results = await promiseWithRetry(sentry500ReportPromise, 3, false);

            const items = buildItems(results.sentries, results.sentinelStatus);

            totalCount = items.length;
            service.reports = items;
            service.reportsCache = items.slice();
            service.isFull = service.reports.length >= maxReportCapacity;

            service.filter.setFilterByDeviceType(defaultDeviceTypeFilter(items));
        } catch (e) {
            service.reports = [];
            service.reportsCache = [];
            if (e && e.status === 404) {
                service.dataWarning = `No device reports found for date range'`;
            } else {
                console.log(e);
                service.dataError = 'Error loading device reports';
            }
        } finally {
            $rootScope.$broadcast('latestTrackingDeviceReportsLoaded', service.reports);
            loadingComplete();
        }
    }

    async function searchPattern(pattern) {
        console.log(`searchPattern items start`, pattern);
        loadingBegin();

        try {
            const results = await ShipmentsReportService.searchDeviceReports(SentinelUiSession.focus.id, pattern).$promise;
            const items = buildItems(results.sentries, results.sentinelStatus);
            if (items.length > 0) {
                _.forEach(items, function (i) {
                    totalCount++;
                    service.reports.push(i);
                });
            }
        } catch (e) {
            service.reports = [];
            service.reportsCache = [];
            if (e && e.status === 404) {
                service.dataWarning = `No device reports found for '${pattern}'`;
            } else {
                console.log(e);
                service.dataError = 'Error loading device reports';
            }
        } finally {
            $rootScope.$broadcast('latestTrackingDeviceReportsLoaded', service.reports);
            loadingComplete();
        }
    }

    function filterCurrentResult(fromMoment, toMoment) {
        if (!service.reports) {
            return;
        }

        service.reports = service.reportsCache.filter(function (r) {
            const timeOfReceipt = moment(r.serverTimeStamp);
            return !!(fromMoment.isSameOrBefore(timeOfReceipt) && timeOfReceipt.isSameOrBefore(toMoment));

        });
        service.isFull = service.reports.length >= maxReportCapacity;
        $rootScope.$broadcast('latestTrackingDeviceReportsLoaded', service.reports);
        loadingComplete();
    }

    async function load(fromDate, toDate, refresh) {
        if (!fromDate || !toDate) {
            clear();
            return;
        }

        const oneYearAgo = moment().subtract(1, 'year').subtract(1, 'day');
        const fromMoment = moment.isMoment(fromDate) ? fromDate : moment(fromDate);
        const toMoment = moment.isMoment(toDate) ? toDate : moment(toDate);
        const maxToMoment = moment(fromMoment).add(service.maxDays, 'day');

        if (fromMoment.isBefore(oneYearAgo) || toMoment.isBefore(fromMoment) || toMoment.isAfter(maxToMoment)) {
            return;
        }

        if (!refresh && service.lastClientGuid === SentinelUiSession.focus.id &&
            fromMoment.isSame(service.fromDate) && toMoment.isSame(service.toDate)) {
            //do not reload the same data set again
            return;
        }

        service.lastid = SentinelUiSession.focus.id;
        service.selected = null;
        service.fromDate = fromMoment;
        service.toDate = toMoment;
        totalCount = 0;

        if (service.fromDateCache && service.fromDateCache.isBefore(fromMoment) &&
            service.toDateCache && toMoment.isBefore(service.toDateCache) && service.reportsCache.length > 0) {
            filterCurrentResult(fromMoment, toMoment);
            return;
        }

        loadingBegin();

        service.fromDateCache = service.fromDate.clone();
        service.toDateCache = service.toDate.clone();

        const promise = SentinelUiSession.user.isAnAdmin ?
            ClientReportsService.getLatestSentry500ReportsByClient(SentinelUiSession.focus, service.fromDate, service.toDate, 1, maxPageSize).$promise :
            ClientReportsService.getLatestSentry500Reports(SentinelUiSession.focus, service.fromDate, service.toDate, 1, maxPageSize).$promise;

        try {
            const results = await promise;

            const items = buildItems(results.sentries, results.sentinelStatus);

            totalCount = items.length;
            service.reports = items;
            service.reportsCache = items.slice();
            service.isFull = service.reports.length >= maxReportCapacity;
        } catch (e) {
            service.reports = [];
            service.reportsCache = [];
            if (e && e.status === 404) {
                service.dataWarning = `No device reports found for date range'`;
            } else {
                console.log(e);
                service.dataError = 'Error loading device reports';
            }
        } finally {
            $rootScope.$broadcast('latestTrackingDeviceReportsLoaded', service.reports);
            loadingComplete();
        }
    }

    function filterData(type) {
        let data = [];
        data = _.filter(service.allReports, function (report) {
            if (report.type === type) {
                return report;
            }
        });

        return data;
    }

    function getReport(guid) {
        const target = `${guid}`
        return service.reports.find(report => `${report.guid}` === target);
    }
}
